import { WORK_ITEM_STATUSES, type WorkItemStatus, type WorkItemView } from '@hilo/contracts';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { type CSSProperties, type DragEvent, type KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { errorText } from '../../lib/copy';
import { checkLiveSession, useCurrentUser } from '../../lib/session';
import { startLive } from '../../lib/socket';
import {
  addDays, canManageWork, canMoveWork, COLUMN_PAGE, dueTone, fetchColumn, fetchProjects, fetchSummary, formatDay, isRevisionConflict,
  PRIORITY_LABEL, RECENT_DONE_DAYS, refreshWork, STATUS_HINT, STATUS_LABEL, todayIn, updateWorkItem, type WorkFilters,
  type WorkFocus, workKeys,
} from '../../lib/workItems';
import { hueOf, initialsOf } from '../../ui/Avatar';
import { ConfirmDialog } from '../../ui/ConfirmDialog';
import { IconSearch } from '../../ui/icons';
import { Select } from '../../ui/Select';
import { useNow } from '../../ui/useNow';
import { Rail } from '../inbox/Rail';
import styles from './Board.module.css';
import { CreateWorkItemDialog } from './CreateWorkItemDialog';
import { WorkItemPanel } from './WorkItemPanel';

/** Indicadores del pulso: nombre en la URL (?ver=) y filtro de la API. */
const FOCUS: Record<string, WorkFocus> = { vencidas: 'overdue', entregas: 'dueWeek', publica: 'publishWeek' };
/** Personas visibles en el pulso antes de «+N más». */
const TEAM_SHOWN = 6;
/** Duración de la salida del detalle (igual que en Board.module.css). */
const PANEL_EXIT_MS = 180;

type Moves = Record<string, { item: WorkItemView; to: WorkItemStatus }>;

const SCOPES = [
  { value: '', label: 'Todas' },
  { value: 'me', label: 'Mis tareas' },
  { value: 'none', label: 'Sin responsable' },
] as const;

const DUE_TEXT = { overdue: 'Vencida', today: 'Entrega hoy', soon: 'Entrega', later: 'Entrega' } as const;

function Card({ item, today, movable, open, onOpen, onMove }: {
  item: WorkItemView; today: string; movable: boolean; open: boolean; onOpen: () => void; onMove: (to: WorkItemStatus) => void;
}) {
  const tone = dueTone(item, today);
  const index = WORK_ITEM_STATUSES.indexOf(item.status);
  // Alt + flechas mueve la tarjeta a la columna vecina (la alternativa de teclado a arrastrar).
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (!movable || !event.altKey || (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft')) return;
    const next = WORK_ITEM_STATUSES[index + (event.key === 'ArrowRight' ? 1 : -1)];
    if (!next) return;
    event.preventDefault();
    onMove(next);
  };
  return (
    <li className={styles.cardItem} data-card={item.id}>
      <button type="button" className={styles.card} data-open={open || undefined} data-priority={item.priority} data-done={item.status === 'DONE' || undefined}
        draggable={movable} aria-current={open ? 'true' : undefined}
        aria-keyshortcuts={movable ? 'Alt+ArrowLeft Alt+ArrowRight' : undefined}
        onDragStart={(event) => { event.dataTransfer.setData('application/json', JSON.stringify(item)); event.dataTransfer.setData('text/plain', item.id); event.dataTransfer.effectAllowed = 'move'; }}
        onClick={onOpen} onKeyDown={onKeyDown}>
        {item.project && <span className={styles.project}>{item.project}</span>}
        <span className={styles.cardTitle}>{item.title}</span>
        <span className={styles.cardMeta}>
          {(item.priority === 'URGENT' || item.priority === 'HIGH') && (
            <span className={styles.priority} data-priority={item.priority}>{PRIORITY_LABEL[item.priority]}</span>
          )}
          {item.dueDate && (
            <span className={styles.due} data-tone={tone ?? undefined}>
              {tone === 'today' ? DUE_TEXT.today : `${tone ? DUE_TEXT[tone] : 'Entrega'} ${formatDay(item.dueDate, today)}`}
            </span>
          )}
          {item.publishDate && <span className={styles.publish}>Publica {formatDay(item.publishDate, today)}</span>}
          {item.commentCount > 0 && (
            <span className={styles.comments}>
              <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" strokeWidth={2} strokeLinejoin="round" aria-hidden="true" focusable="false">
                <path d="M4 5h16v11H9l-5 4z" />
              </svg>
              {item.commentCount}<span className="visually-hidden"> {item.commentCount === 1 ? 'comentario' : 'comentarios'}</span>
            </span>
          )}
          <span className={styles.spacer} />
          {item.assignee
            ? (
              <span className={styles.face} style={{ '--hue': hueOf(item.assignee.id) } as CSSProperties} title={item.assignee.displayName}>
                <span aria-hidden="true">{initialsOf(item.assignee.displayName)}</span>
                <span className="visually-hidden">Responsable: {item.assignee.displayName}</span>
              </span>
            )
            : <span className={styles.nobody}>Sin responsable</span>}
        </span>
      </button>
    </li>
  );
}


/**
 * Una columna con sus propias páginas: pinta como mucho lo cargado (30 en 30) y tiene scroll propio.
 * `moves` son las tarjetas que se acaban de mover y aún no confirmó el servidor: salen de su columna y entran en la nueva.
 */
function Column({ status, filters, focus, since, count, today, moves, openId, dropping, manage, canMove, onOpen, onMove, onCreate,
  onShowAll, onShowRecent, onDragState, onDrop, register }: {
  status: WorkItemStatus; filters: WorkFilters; focus: WorkFocus | null; since: string | null; count: number | undefined;
  today: string; moves: Moves; openId: string | null; dropping: boolean; manage: boolean; canMove: (item: WorkItemView) => boolean;
  onOpen: (id: string) => void; onMove: (item: WorkItemView, to: WorkItemStatus) => void; onCreate: () => void;
  onShowAll: () => void; onShowRecent: () => void; onDragState: (over: boolean) => void; onDrop: (event: DragEvent) => void;
  register: (element: HTMLElement | null) => void;
}) {
  const query = useInfiniteQuery({
    queryKey: workKeys.column(status, filters, focus, since),
    queryFn: ({ pageParam, signal }) => fetchColumn(status, filters, { focus, completedSince: since, cursor: pageParam }, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    refetchInterval: 30_000,
  });
  const loaded = (query.data?.pages ?? []).flatMap((page) => page.items);
  const incoming = Object.values(moves).filter((move) => move.to === status && !loaded.some((item) => item.id === move.item.id))
    .map((move) => ({ ...move.item, status }));
  const cards = [...incoming, ...loaded.filter((item) => !moves[item.id] || moves[item.id]!.to === status)];
  const remaining = count === undefined ? null : Math.max(0, count - loaded.length);

  return (
    <section ref={register} className={styles.column} data-status={status} data-drop={dropping || undefined} aria-labelledby={`columna-${status}`}
      onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = 'move'; if (!dropping) onDragState(true); }}
      onDragLeave={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) onDragState(false); }}
      onDrop={onDrop}>
      <header className={styles.columnHead}>
        <h2 id={`columna-${status}`} className={styles.columnTitle} title={STATUS_HINT[status]}>
          <span className={styles.statusDot} aria-hidden="true" />
          {STATUS_LABEL[status]}
          <span className={styles.count}>{(count ?? cards.length).toLocaleString('es-SV')}</span>
        </h2>
        {manage && (
          <button type="button" className={styles.columnAdd} onClick={onCreate} aria-label={`Nueva tarea en ${STATUS_LABEL[status]}`} title="Nueva tarea">+</button>
        )}
      </header>
      <div className={styles.columnBody}>
        {query.isPending && <p className={styles.emptyColumn}>Cargando…</p>}
        {query.isError && <p className={styles.emptyColumn} role="alert">No se pudo cargar. {errorText(query.error)}</p>}
        {query.isSuccess && cards.length === 0 && (
          <p className={styles.emptyColumn}>{status === 'DONE' ? (since ? 'Nada completado en los últimos días.' : 'Aquí llega lo entregado.') : 'Nada por aquí.'}</p>
        )}
        {cards.length > 0 && (
          <ul className={styles.cards}>
            {cards.map((item) => (
              <Card key={item.id} item={item} today={today} movable={canMove(item)} open={item.id === openId}
                onOpen={() => onOpen(item.id)} onMove={(to) => onMove(item, to)} />
            ))}
          </ul>
        )}
        {query.hasNextPage && (
          <button type="button" className={`btn btn-secondary ${styles.more}`} disabled={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>
            {query.isFetchingNextPage
              ? 'Cargando…'
              : `Mostrar ${Math.min(COLUMN_PAGE, remaining ?? COLUMN_PAGE)} más${remaining ? ` (quedan ${remaining.toLocaleString('es-SV')})` : ''}`}
          </button>
        )}
        {status === 'DONE' && query.isSuccess && (
          <p className={styles.history}>
            {since
              ? <>Solo lo completado en los últimos {RECENT_DONE_DAYS} días. <button type="button" className={styles.linkButton} onClick={onShowAll}>Ver anteriores</button></>
              : <>Mostrando todo lo completado. <button type="button" className={styles.linkButton} onClick={onShowRecent}>Ver solo lo reciente</button></>}
          </p>
        )}
      </div>
    </section>
  );
}

/** Tablero del equipo: en qué está cada quien, con entregas y publicaciones. Lo ve todo el equipo. */
export function BoardPage() {
  const user = useCurrentUser();
  const client = useQueryClient();
  const now = useNow(60_000);
  const today = todayIn(user.timezone, new Date(now));
  const manage = canManageWork(user.role);
  const [params, setParams] = useSearchParams();
  const filters: WorkFilters = {
    ...(params.get('de') ? { assignee: params.get('de')! } : {}),
    ...(params.get('proyecto') ? { project: params.get('proyecto')! } : {}),
    ...(params.get('buscar') ? { q: params.get('buscar')! } : {}),
  };
  const focusKey = params.get('ver');
  const focus = (focusKey && FOCUS[focusKey]) || null;
  const since = params.get('historial') ? null : addDays(today, -(RECENT_DONE_DAYS - 1));
  const openId = params.get('tarea');
  const [search, setSearch] = useState(filters.q ?? '');
  const [creating, setCreating] = useState<WorkItemStatus | null>(null);
  const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [dropTarget, setDropTarget] = useState<WorkItemStatus | null>(null);
  const [mobileStatus, setMobileStatus] = useState<WorkItemStatus>('TODO');
  const [moves, setMoves] = useState<Moves>({});
  const [teamOpen, setTeamOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const columns = useRef<Partial<Record<WorkItemStatus, HTMLElement | null>>>({});
  const lastCard = useRef<string | null>(null);

  const summary = useQuery({
    queryKey: workKeys.summary(filters, focus, since),
    queryFn: () => fetchSummary(filters, focus, since),
    refetchInterval: 30_000,
  });
  const projects = useQuery({ queryKey: workKeys.projects, queryFn: fetchProjects, staleTime: 60_000 });
  const stats = summary.data;

  const setParam = (key: string, value: string | null) => setParams((current) => {
    const next = new URLSearchParams(current);
    if (value) next.set(key, value);
    else next.delete(key);
    return next;
  }, { replace: key !== 'tarea' });

  // El aviso de «pasó a…» se va solo; los errores se quedan hasta la siguiente acción.
  useEffect(() => {
    if (notice?.tone !== 'ok') return;
    const timer = window.setTimeout(() => setNotice(null), 4000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  // La búsqueda se aplica al dejar de escribir.
  useEffect(() => {
    if (search.trim() === (filters.q ?? '')) return;
    const timer = window.setTimeout(() => setParam('buscar', search.trim() || null), 300);
    return () => window.clearTimeout(timer);
  });

  // En vivo: cualquier cambio de una tarea vuelve a pedir columnas y resumen.
  useEffect(() => startLive({
    onChange: (change) => { if (change.kind === 'workItem') void refreshWork(client); },
    onReconnect: () => void refreshWork(client),
    onRejected: (signal) => checkLiveSession(client, signal),
  }), [client]);

  // Cambios sin guardar en el detalle: antes de cerrarlo o cambiar de tarea se pregunta.
  const dirty = useRef(false);
  const onDirtyChange = useCallback((value: boolean) => { dirty.current = value; }, []);
  const [pendingLeave, setPendingLeave] = useState<(() => void) | null>(null);
  const guard = (action: () => void) => {
    if (dirty.current) setPendingLeave(() => action);
    else action();
  };
  const panelRef = useRef<HTMLDivElement>(null);

  const doOpen = (id: string) => {
    lastCard.current = id;
    setClosing(false);
    setParam('tarea', id);
  };
  // El detalle sale deslizándose antes de desmontarse; el foco vuelve a la tarjeta que se abrió.
  const doClose = () => {
    setClosing(true);
    // Se queda en el último fotograma de «sale» (forwards) hasta desmontarse: si «closing» volviera a false
    // antes de quitar la tarea de la URL, el panel reaparecería un fotograma (el parpadeo).
    window.setTimeout(() => {
      setParam('tarea', null);
      window.requestAnimationFrame(() => document.querySelector<HTMLElement>(`[data-card="${lastCard.current}"] button`)?.focus());
    }, PANEL_EXIT_MS);
  };
  if (!openId && closing) setClosing(false);
  const open = (id: string) => { if (id !== openId) guard(() => doOpen(id)); };
  const close = () => { if (!closing) guard(doClose); };

  // Clic fuera del detalle (en el tablero o la cabecera) lo cierra; las tarjetas y los diálogos tienen su propio manejo.
  const closeRef = useRef(close);
  useEffect(() => { closeRef.current = close; });
  useEffect(() => {
    if (!openId) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Element | null;
      if (!target || panelRef.current?.contains(target) || target.closest('dialog, [data-card]')) return;
      closeRef.current();
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [openId]);

  const move = async (item: WorkItemView, to: WorkItemStatus) => {
    if (item.status === to || !canMoveWork(user, item)) return;
    // Se mueve al instante; si la API lo rechaza, vuelve a su sitio.
    setMoves((current) => ({ ...current, [item.id]: { item, to } }));
    setNotice({ tone: 'ok', text: `«${item.title}» pasó a ${STATUS_LABEL[to]}.` });
    try {
      await updateWorkItem(item.id, item.revision, { status: to });
    } catch (error) {
      setNotice({
        tone: 'error',
        text: isRevisionConflict(error)
          ? `«${item.title}» cambió mientras tanto. Se recargó el tablero; vuelve a moverla si hace falta.`
          : `No se pudo mover «${item.title}». ${errorText(error)}`,
      });
    }
    await refreshWork(client);
    setMoves(({ [item.id]: _done, ...rest }) => rest);
  };

  const onDrop = (status: WorkItemStatus) => (event: DragEvent) => {
    event.preventDefault();
    setDropTarget(null);
    try {
      const item = JSON.parse(event.dataTransfer.getData('application/json')) as WorkItemView;
      if (item?.id) void move(item, status);
    } catch {
      // Lo soltado no es una tarjeta del tablero.
    }
  };

  const showColumn = (status: WorkItemStatus) => {
    setMobileStatus(status);
    columns.current[status]?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
  };

  const workload = stats?.workload ?? [];
  const team = teamOpen ? workload : workload.slice(0, TEAM_SHOWN);
  const dateLabel = new Intl.DateTimeFormat('es-SV', { timeZone: user.timezone, weekday: 'long', day: 'numeric', month: 'long' }).format(new Date(now));
  const scope = filters.assignee === 'me' || filters.assignee === 'none' ? filters.assignee : '';
  const pulseStats = stats ? [
    { key: 'vencidas', count: stats.overdue, tone: 'overdue', text: stats.overdue === 1 ? 'vencida' : 'vencidas' },
    { key: 'entregas', count: stats.dueThisWeek, tone: 'due', text: stats.dueThisWeek === 1 ? 'se entrega esta semana' : 'se entregan esta semana' },
    { key: 'publica', count: stats.publishThisWeek, tone: 'publish', text: stats.publishThisWeek === 1 ? 'se publica esta semana' : 'se publican esta semana' },
  ] : [];

  return (
    <div className={styles.app}>
      <Rail />
      <main className={styles.main} data-panel={openId ? 'open' : undefined}>
        <div className={styles.boardArea}>
          <header className={styles.header}>
            <h1 className={styles.title}>
              Tablero
              <span className={styles.date}>{dateLabel}</span>
            </h1>
            {manage && (
              <button type="button" className={`btn btn-primary ${styles.add}`} onClick={() => setCreating('TODO')}>
                <span aria-hidden="true" className={styles.plus}>+</span>Nueva tarea
              </button>
            )}
          </header>

          {/* El pulso del equipo: cada indicador y cada persona filtra el tablero al pulsarlo. */}
          {stats && (
            <div className={styles.pulse}>
              <div className={styles.stats} role="group" aria-label="Ver solo">
                {pulseStats.map((stat) => (
                  <button key={stat.key} type="button" className={styles.stat} data-tone={stat.tone} data-zero={stat.count === 0 || undefined}
                    aria-pressed={focusKey === stat.key} onClick={() => setParam('ver', focusKey === stat.key ? null : stat.key)}>
                    <span className={styles.statCount}>{stat.count.toLocaleString('es-SV')}</span>
                    <span className={styles.statText}>{stat.text}</span>
                    {focusKey === stat.key && <span className={styles.clear} aria-hidden="true">×</span>}
                  </button>
                ))}
              </div>
              {workload.length > 0 && (
                <div className={styles.team} role="group" aria-label="Tareas abiertas por persona">
                  {team.map((entry) => (
                    <button key={entry.id} type="button" className={styles.member} aria-pressed={filters.assignee === entry.id}
                      title={`${entry.displayName}: ${entry.open === 1 ? '1 tarea abierta' : `${entry.open} tareas abiertas`}`}
                      onClick={() => setParam('de', filters.assignee === entry.id ? null : entry.id)}>
                      <span className={styles.memberFace} style={{ '--hue': hueOf(entry.id) } as CSSProperties} aria-hidden="true">
                        {initialsOf(entry.displayName)}
                      </span>
                      <span className={styles.memberName}>{entry.id === user.id ? 'Tú' : entry.displayName.split(' ')[0]}</span>
                      <span className={styles.memberCount}>{entry.open}</span>
                      {filters.assignee === entry.id && <span className={styles.clear} aria-hidden="true">×</span>}
                      <span className="visually-hidden"> tareas abiertas de {entry.displayName}</span>
                    </button>
                  ))}
                  {workload.length > TEAM_SHOWN && (
                    <button type="button" className={styles.moreTeam} aria-expanded={teamOpen} onClick={() => setTeamOpen(!teamOpen)}>
                      {teamOpen ? 'Ver menos' : `+${workload.length - TEAM_SHOWN} más`}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          <div className={styles.filters}>
            <div className={styles.scopes} role="group" aria-label="Qué tareas ver">
              {SCOPES.map((option) => (
                <button key={option.value} type="button" className={styles.scope} aria-pressed={scope === option.value}
                  onClick={() => setParam('de', option.value || null)}>
                  {option.label}
                </button>
              ))}
            </div>
            <Select variant="pill" label="Proyecto" hideLabel active={!!filters.project} value={filters.project ?? ''}
              onChange={(value) => setParam('proyecto', value || null)}
              options={[{ value: '', label: 'Todos los proyectos' }, ...(projects.data ?? []).map((name) => ({ value: name, label: name }))]} />
            <label className={styles.search}>
              <IconSearch className={styles.searchIcon} />
              <span className="visually-hidden">Buscar tareas por título o proyecto</span>
              <input type="search" placeholder="Buscar tareas" value={search} maxLength={100} onChange={(event) => setSearch(event.target.value)} />
            </label>
          </div>

          <p className={styles.notice} data-tone={notice?.tone} role={notice?.tone === 'error' ? 'alert' : 'status'}>
            {notice?.text}
          </p>

          {summary.isError && <p className={styles.muted} role="alert">No se pudo cargar el resumen del tablero. {errorText(summary.error)}</p>}

          {/* Móvil: una columna a la vez; las pestañas llevan a cada una (también se desliza). */}
          <div className={styles.statusTabs} role="group" aria-label="Columnas">
            {WORK_ITEM_STATUSES.map((status) => (
              <button key={status} type="button" className={styles.statusTab} aria-pressed={mobileStatus === status}
                data-status={status} onClick={() => showColumn(status)}>
                {STATUS_LABEL[status]}
                <span className={styles.tabCount}>{stats ? stats.byStatus[status].toLocaleString('es-SV') : '·'}</span>
              </button>
            ))}
          </div>

          <div className={styles.board}
            onScroll={(event) => {
              const element = event.currentTarget;
              const index = Math.round(element.scrollLeft / Math.max(1, element.scrollWidth / WORK_ITEM_STATUSES.length));
              const status = WORK_ITEM_STATUSES[Math.min(index, WORK_ITEM_STATUSES.length - 1)];
              if (status && status !== mobileStatus) setMobileStatus(status);
            }}>
            {WORK_ITEM_STATUSES.map((status) => (
              <Column key={status} status={status} filters={filters} focus={focus} since={since}
                count={stats?.byStatus[status]} today={today} moves={moves} openId={openId} dropping={dropTarget === status} manage={manage}
                canMove={(item) => canMoveWork(user, item)} onOpen={open} onMove={(item, to) => void move(item, to)}
                onCreate={() => setCreating(status)} onShowAll={() => setParam('historial', '1')} onShowRecent={() => setParam('historial', null)}
                onDragState={(over) => setDropTarget(over ? status : null)} onDrop={onDrop(status)}
                register={(element) => { columns.current[status] = element; }} />
            ))}
          </div>
          <p className={styles.keyHint}>Arrastra una tarjeta para cambiar su estado, o usa <kbd>Alt</kbd> + <kbd>←</kbd> <kbd>→</kbd> sobre ella.</p>
        </div>

        {openId && (
          <div className={styles.panel} ref={panelRef} data-closing={closing || undefined}>
            <WorkItemPanel key={openId} id={openId} onClose={close} onDirtyChange={onDirtyChange} />
          </div>
        )}
      </main>

      {pendingLeave && (
        <ConfirmDialog open icon={<span aria-hidden="true">!</span>} title="Tienes cambios sin guardar"
          confirmLabel="Descartar cambios" cancelLabel="Seguir editando"
          onConfirm={() => { const leave = pendingLeave; dirty.current = false; setPendingLeave(null); leave(); }}
          onCancel={() => setPendingLeave(null)}>
          <p>Si cierras esta tarea ahora, se pierde lo que cambiaste o escribiste y no guardaste.</p>
        </ConfirmDialog>
      )}
      {creating && (
        <CreateWorkItemDialog defaultStatus={creating} onClose={() => setCreating(null)}
          onCreated={(item) => { setCreating(null); open(item.id); }} />
      )}
    </div>
  );
}
